import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  Grid,
  TextField,
} from "@material-ui/core";
import { Icon } from "@iconify/react";
import AddIcon from "@material-ui/icons/Add";
import { Autocomplete } from "@material-ui/lab";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { useRouteMatch } from "react-router-dom";

import ProductListItem from "./ProductListItem";
import { CustomTheme } from "../../../../../../types/customTheme";
import DialogCommon from "../../../../../../components/dialogs/DialogCommon";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";
import TextfieldDefaultNew from "../../../../../../components/textField/TextfieldDefaultNew";
import ValidationMessage from "../../../../../../components/validation/ValidationMessage";
import DefaultAlert from "../../../../../../components/alerts/DefaultAlert";
import {
  createProductWrapper,
  createWProduct,
} from "../../../../../../services/menuApp/menuService";
import {
  ERROR_MESSAGE_UNEXPECTED_ERROR,
  SUCCESSFULLY_CREATED,
} from "../../../../../../utils/consts";
import { updateDirectoryInStore } from "../../../../../../redux/actions/menu";
import DialogCommonDefault from "../../../../../../components/dialogs/DialogCommonDefault";

interface ProductsSectionProps {
  selectedProduct: any[];
  expandedProductIds: Set<string | number>;
  onExpandToggle: (id: string | number) => void;
  stockDepartmentSelectedNode: any[];
  selectedDepartmentObj: any;
  onDepartmentChange: (dept: any, productId: string) => void;
  classes: any;
  calculateMargin: (price: number, cost: number) => string;
  onProductFieldChange: (field: string, value: any, productId: string) => void;
  onImmediateProductValidToggle?: (
    productId: string,
    checked: boolean,
    product: any,
  ) => void;
  isEditDisabled: boolean;
  menuId?: string;
  categoryId?: any;
  directoryId?: any;
  directoryLevel?: number;
  isRoot?: boolean;
  firstWrapperId?: any;
  wrapperId?: any;
  onProductAdded?: (newProduct?: any) => void;
  menuNodeList?: any;
  setMenuNodeList?: any;
  selectedMenuId?: string;
  setIsOpenDirectoryOrProductSelectionModal?: any;
  isProductAddModalOpen: boolean;
  setIsProductAddModalOpen?: any;
  wrapperData?: any;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({
  selectedProduct,
  expandedProductIds,
  onExpandToggle,
  stockDepartmentSelectedNode,
  selectedDepartmentObj,
  onDepartmentChange,
  classes,
  calculateMargin,
  onProductFieldChange,
  onImmediateProductValidToggle,
  isEditDisabled,
  menuId,
  categoryId,
  directoryId,
  directoryLevel,
  isRoot,
  firstWrapperId,
  wrapperId,
  onProductAdded,
  isProductAddModalOpen,
  setIsProductAddModalOpen,
  wrapperData,
}) => {
  const theme: CustomTheme = useTheme();
  const match: any = useRouteMatch();
  const dispatch = useDispatch();
  const locationId = match.params.locationId;

  // Modal states
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [productName, setProductName] = useState("");
  const [productPlu, setProductPlu] = useState("");
  const [productBarcode, setProductBarcode] = useState("");
  const [productUpcBarcode, setProductUpcBarcode] = useState("");
  const [productNameShort, setProductNameShort] = useState("");
  const [isProductNameEmpty, setIsProductNameEmpty] = useState(false);
  // ✅ Initialize selectedDepartment with selectedDepartmentObj (first product's department)
  const [selectedDepartment, setSelectedDepartment] = useState<any>(
    selectedDepartmentObj || {},
  );
  const [isDepartmentEmpty, setIsDepartmentEmpty] = useState(false);
  const [openDepartmentDropdown, setOpenDepartmentDropdown] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // New states for additional fields
  const [productSize, setProductSize] = useState("");
  const [productPrintName, setProductPrintName] = useState("");
  const [productGlobalProduct, setProductGlobalProduct] = useState("");
  const [productClassification, setProductClassification] =
    useState("Main Course");
  const [productPrice, setProductPrice] = useState("");
  const [productCost, setProductCost] = useState("");
  const [productInTax, setProductInTax] = useState("0%");
  const [productOutTax, setProductOutTax] = useState("20%");
  const [productDescription, setProductDescription] = useState("");

  // New states for additional API fields
  const [productExtId, setProductExtId] = useState("");
  const [productGlobalProductId, setProductGlobalProductId] = useState("");
  const [productClassificationId, setProductClassificationId] = useState("");

  // Price validation state
  const [isPriceEmpty, setIsPriceEmpty] = useState(false);

  // Helper function to extract ID from categoryId
  const getCategoryIdValue = (catId: any): number => {
    if (!catId) return 0;
    if (typeof catId === "number") return catId;
    if (typeof catId === "object" && catId.id) return catId.id;
    return 0;
  };

  const handleOpenProductAddModal = () => {
    if (isEditDisabled) return;
    setIsProductAddModalOpen(true);
    // Reset all fields
    setProductName("");
    setProductPlu("");
    setProductBarcode("");
    setProductUpcBarcode("");
    setProductNameShort("");
    setProductSize("");
    setProductPrintName("");
    setProductGlobalProduct("");
    setProductClassification("Main Course");
    setProductPrice("");
    setProductCost("");
    setProductInTax("0%");
    setProductOutTax("20%");
    setProductDescription("");
    setProductExtId("");
    setProductGlobalProductId("");
    setProductClassificationId("");
    // ✅ Keep the department from first product
    setSelectedDepartment(selectedDepartmentObj || {});
    setIsProductNameEmpty(false);
    setIsDepartmentEmpty(false);
    setIsPriceEmpty(false);
  };

  const handleCloseProductAddModal = () => {
    setIsProductAddModalOpen(false);
    setProductName("");
    setProductPlu("");
    setProductBarcode("");
    setProductUpcBarcode("");
    setProductNameShort("");
    setProductSize("");
    setProductPrintName("");
    setProductGlobalProduct("");
    setProductClassification("Main Course");
    setProductPrice("");
    setProductCost("");
    setProductInTax("0%");
    setProductOutTax("20%");
    setProductDescription("");
    setProductExtId("");
    setProductGlobalProductId("");
    setProductClassificationId("");
    // ✅ Keep the department from first product
    setSelectedDepartment(selectedDepartmentObj || {});
    setIsProductNameEmpty(false);
    setIsDepartmentEmpty(false);
    setIsPriceEmpty(false);
  };

  const handleDepartmentChange = (e: any, departmentData: any) => {
    setIsDepartmentEmpty(false);
    setSelectedDepartment(departmentData);
  };

  const handleAddProduct = async () => {
    // Validate
    let hasError = false;

    if (!selectedDepartment?.id) {
      setIsDepartmentEmpty(true);
      hasError = true;
    }

    // Price validation - required field
    if (
      !productPrice ||
      productPrice.trim() === "" ||
      parseFloat(productPrice) <= 0
    ) {
      setIsPriceEmpty(true);
      hasError = true;
    }

    if (hasError) return;

    setIsLoadingButton(true);

    // Get the actual category ID value
    const actualCategoryId = getCategoryIdValue(categoryId);

    // Get itemWrapperId from selectedProduct
    let itemWrapperId = null;
    if (
      selectedProduct &&
      selectedProduct.length > 0 &&
      selectedProduct[0]?.itemWrapperId
    ) {
      if (
        typeof selectedProduct[0].itemWrapperId === "object" &&
        selectedProduct[0].itemWrapperId.id
      ) {
        itemWrapperId = selectedProduct[0].itemWrapperId.id;
      } else {
        itemWrapperId = selectedProduct[0].itemWrapperId;
      }
    }
    if (!itemWrapperId && wrapperId) {
      itemWrapperId = wrapperId;
    }

    // Updated body with all required fields
    const body: any = {
      name: wrapperData.name,
      locationId: locationId,
      valid: true,
      plu: productPlu || "",
      barcode: productBarcode || "",
      upcBarcode: productUpcBarcode || "",
      departmentId: selectedDepartment.id,
      categoryId: actualCategoryId,
      // nameShort: productNameShort || "",
      size: productSize || "",
      desc: "",
      // price: productPrice ? parseFloat(productPrice) : 0,
      // cost: productCost ? parseFloat(productCost) : 0,
      // margin:
      //   productPrice && productCost
      //     ? calculateMargin(parseFloat(productPrice), parseFloat(productCost))
      //     : "0",
      // inTax: productInTax ? parseInt(productInTax.replace("%", "")) : 0,
      // outTax: productOutTax ? parseInt(productOutTax.replace("%", "")) : 20,

      extId: productExtId || "",
      globalProductId: productGlobalProductId || "",
      classificationId: productClassificationId || "",
      itemWrapperId: itemWrapperId || undefined,
    };

    // Add directory fields if not root
    if (!isRoot && directoryId) {
      body.directoryId = directoryId;
      body.directoryLevel = directoryLevel || 1;
    }

    // Add additional product fields
    if (productSize) body.size = productSize;
    if (productPrintName) body.printName = productPrintName;
    // if (productGlobalProduct) body.globalProduct = productGlobalProduct;
    // if (productClassification) body.classification = productClassification;
    if (productPrice) body.price = parseFloat(productPrice);
    if (productCost) body.cost = parseFloat(productCost);
    if (productInTax) body.inTax = parseInt(productInTax.replace("%", ""));
    if (productOutTax) body.outTax = parseInt(productOutTax.replace("%", ""));
    // if (productDescription) body.desc = productDescription;

    console.log("📦 Product creation payload:", body);

    try {
      const summaryString = `${productName} Product wrapper is added`;

      const res = await createWProduct(
        locationId,
        body,
        "Menus Changed",
        summaryString,
        "menu",
      );

      const newProduct = res.data.data;

      // Normalize response data
      let normalizedProduct = { ...newProduct };
      if (
        normalizedProduct.categoryId &&
        typeof normalizedProduct.categoryId === "object"
      ) {
        normalizedProduct.categoryId = normalizedProduct.categoryId.id;
      }
      if (
        normalizedProduct.itemWrapperId?.categoryId &&
        typeof normalizedProduct.itemWrapperId.categoryId === "object"
      ) {
        normalizedProduct.itemWrapperId.categoryId =
          normalizedProduct.itemWrapperId.categoryId.id;
      }

      // Get the wrapper data
      const newWrapper = normalizedProduct.itemWrapperId || normalizedProduct;

      // Update Redux store
      const newItemForRedux = {
        id: newWrapper.id,
        valid: newWrapper.valid !== undefined ? newWrapper.valid : true,
        version: newWrapper.version || 0,
        name: productName.trim(),
        colour: newWrapper.colour || "",
        img: newWrapper.img || "",
        desc: newWrapper.desc || "",
        directory:
          newWrapper.directory === true || newWrapper.directory === "true",
        directoryId: newWrapper.directoryId || -99,
        directoryLevel: newWrapper.directoryLevel || 1,
        itemType: newWrapper.itemType || "",
        allYouCanEat: newWrapper.allYouCanEat || false,
        kiosk: newWrapper.kiosk || 1,
        cover: newWrapper.cover || "",
        // nameShort: newWrapper.nameShort || "",
        viewOrder: newWrapper.viewOrder || 0,
        price: normalizedProduct.price || 0,
        size: normalizedProduct.size || "",
        printName: normalizedProduct.printName || "",
        globalProduct: normalizedProduct.globalProduct || "",
        classification: normalizedProduct.classification || "Main Course",
        cost: normalizedProduct.cost || 0,
        inTax: normalizedProduct.inTax || 0,
        outTax: normalizedProduct.outTax || 20,
        extId: normalizedProduct.extId || "",
        globalProductId: normalizedProduct.globalProductId || "",
        classificationId: normalizedProduct.classificationId || "",
      };

      dispatch(updateDirectoryInStore(newItemForRedux));

      // ✅ Close modal immediately
      setIsProductAddModalOpen(false);
      setSuccess(SUCCESSFULLY_CREATED);

      // ✅ Call onProductAdded callback only if product has valid data
      if (onProductAdded) {
        if (normalizedProduct && normalizedProduct.id) {
          onProductAdded(normalizedProduct);
        }
      }

      // Reset form
      setProductName("");
      setProductPlu("");
      setProductBarcode("");
      setProductUpcBarcode("");
      setProductNameShort("");
      setProductSize("");
      setProductPrintName("");
      setProductGlobalProduct("");
      setProductClassification("Main Course");
      setProductPrice("");
      setProductCost("");
      setProductInTax("0%");
      setProductOutTax("20%");
      setProductDescription("");
      setProductExtId("");
      setProductGlobalProductId("");
      setProductClassificationId("");
      // ✅ Keep the department from first product
      setSelectedDepartment(selectedDepartmentObj || {});
      setIsPriceEmpty(false);

      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding product:", err);
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
      setIsLoadingButton(false);
    } finally {
      setIsLoadingButton(false);
    }
  };

  // Calculate margin
  const calculateProductMargin = (price: string, cost: string): string => {
    const priceNum = parseFloat(price) || 0;
    const costNum = parseFloat(cost) || 0;
    if (priceNum > 0 && costNum > 0) {
      return (((priceNum - costNum) / priceNum) * 100).toFixed(1);
    }
    return "0";
  };

  // Filter out empty products
  const validProducts = selectedProduct.filter((product) => product);

  return (
    <>
      <Paper
        elevation={0}
        style={{
          marginBottom: 24,
          borderRadius: 16,
          backgroundColor: theme.palette.background.menuProductColor,
          padding: 24,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Box display="flex" alignItems="center">
            <Box
              style={{
                backgroundColor: "#5e35b1",
                borderRadius: 8,
                padding: 6,
                marginRight: 12,
              }}
            >
              <Icon
                icon="lucide:layers"
                width="20"
                style={{ color: "white" }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                style={{ fontWeight: "bold" }}
                align="left"
              >
                Products ({validProducts.length || 0})
              </Typography>
              <Typography variant="caption">
                Manage products under this wrapper.
              </Typography>
            </Box>
          </Box>
          <Button
            startIcon={<AddIcon style={{ color: "white" }} />}
            style={{
              backgroundColor: "#5e35b1",
              textTransform: "none",
              borderRadius: "8px",
              padding: "6px 16px",
              color: "white",
            }}
            onClick={handleOpenProductAddModal}
            disabled={isEditDisabled}
          >
            Add Product
          </Button>
        </Box>

        {validProducts.map((product, index) => {
          const isExpanded = expandedProductIds.has(product.id || index);

          return (
            <ProductListItem
              key={product.id || index}
              product={product}
              index={index}
              isExpanded={isExpanded}
              isLastItem={index === validProducts.length - 1}
              onExpandToggle={onExpandToggle}
              stockDepartmentSelectedNode={stockDepartmentSelectedNode}
              selectedDepartmentObj={selectedDepartmentObj}
              onDepartmentChange={onDepartmentChange}
              classes={classes}
              calculateMargin={calculateMargin}
              onFieldChange={onProductFieldChange}
              onImmediateValidToggle={onImmediateProductValidToggle}
              isEditDisabled={isEditDisabled}
            />
          );
        })}
      </Paper>

      {/* Add Product Modal with all fields */}
      <DialogCommonDefault
        open={isProductAddModalOpen}
        setOpen={setIsProductAddModalOpen}
        isNeedFixedHeight={false}
        maxWidth="lg"
      >
        <Box p={4}>
          <Typography variant="h6" style={{ marginTop: 0, marginBottom: 16 }}>
            Add New Product
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            Add a new product to this wrapper. Fill in the details below.
          </Typography>

          <Grid container spacing={2}>
            {/* Row 1: Variation Name/Size, Department */}
            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Variation Name/ Size
              </Typography>
              <TextfieldDefaultNew
                id="product-size"
                name="product-size"
                type="text"
                value={productSize}
                onChange={(e: any) => setProductSize(e.target.value)}
                placeholder="Enter size"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Department <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <Autocomplete
                open={openDepartmentDropdown}
                onOpen={() => setOpenDepartmentDropdown(true)}
                onClose={() => setOpenDepartmentDropdown(false)}
                size={"small"}
                id="department-select"
                color="inherit"
                options={stockDepartmentSelectedNode || []}
                value={selectedDepartment}
                getOptionLabel={(option: any) => option?.name || ""}
                disableClearable
                onChange={handleDepartmentChange}
                classes={{ root: classes.autoComplete }}
                renderInput={(params: any) => (
                  <TextField
                    color="inherit"
                    {...params}
                    label=""
                    placeholder="Select Department"
                    variant="outlined"
                  />
                )}
              />
              {isDepartmentEmpty && (
                <ValidationMessage message={"Department is required"} />
              )}
            </Grid>

            {/* Row 2: Print Name, Global Product, Classification */}
            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>Print Name</Typography>
              <TextfieldDefaultNew
                id="product-print-name"
                name="product-print-name"
                type="text"
                value={productPrintName}
                onChange={(e: any) => setProductPrintName(e.target.value)}
                placeholder="Enter print name"
              />
            </Grid>

            {/* <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Global Product
              </Typography>
              <TextfieldDefaultNew
                id="product-global"
                name="product-global"
                type="text"
                value={productGlobalProduct}
                onChange={(e: any) => setProductGlobalProduct(e.target.value)}
                placeholder="Coke Can"
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Classification <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <Autocomplete
                size="small"
                options={[
                  "Main Course",
                  "Appetizer",
                  "Dessert",
                  "Beverage",
                  "Side Dish",
                  "Special",
                ]}
                value={productClassification}
                onChange={(e: any, value: any) => {
                  if (value) setProductClassification(value);
                }}
                classes={{ root: classes.autoComplete }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select classification"
                  />
                )}
              />
            </Grid> */}

            {/* Row 3: Price, Cost, Margin */}
            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Price <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <TextfieldDefaultNew
                id="product-price"
                name="product-price"
                type="text"
                value={productPrice}
                onChange={(e: any) => {
                  setIsPriceEmpty(false);
                  setProductPrice(e.target.value);
                }}
                placeholder="0.00"
              />
              {isPriceEmpty && (
                <ValidationMessage
                  message={"Price is required and must be greater than 0"}
                />
              )}
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>Cost</Typography>
              <TextfieldDefaultNew
                id="product-cost"
                name="product-cost"
                type="text"
                value={productCost}
                onChange={(e: any) => setProductCost(e.target.value)}
                placeholder="0.00"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>Margin</Typography>
              <TextfieldDefaultNew
                id="product-margin"
                name="product-margin"
                type="text"
                value={calculateProductMargin(productPrice, productCost)}
                disabled={true}
              />
            </Grid>

            {/* Row 4: In Tax, Out Tax */}
            <Grid item xs={12} sm={6}>
              <Typography style={{ paddingLeft: "4px" }}>
                In Tax <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <Autocomplete
                size="small"
                options={["0%", "5%", "10%", "20%"]}
                value={productInTax}
                onChange={(e: any, value: any) => {
                  if (value) setProductInTax(value);
                }}
                classes={{ root: classes.autoComplete }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select In Tax"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography style={{ paddingLeft: "4px" }}>
                Out Tax <span style={{ color: "#f44336" }}>*</span>
              </Typography>
              <Autocomplete
                size="small"
                options={["0%", "5%", "10%", "20%"]}
                value={productOutTax}
                onChange={(e: any, value: any) => {
                  if (value) setProductOutTax(value);
                }}
                classes={{ root: classes.autoComplete }}
                renderInput={(params: any) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select Out Tax"
                  />
                )}
              />
            </Grid>

            {/* Row 5: Description (full width) */}
            {/* <Grid item xs={12}>
              <Typography style={{ paddingLeft: "4px" }}>
                Description
              </Typography>
              <TextfieldDefaultNew
                id="product-description"
                name="product-description"
                type="text"
                multiline
                rows={3}
                value={productDescription}
                onChange={(e: any) => setProductDescription(e.target.value)}
                placeholder="Enter product description"
              />
            </Grid> */}

            {/* Row 6: PLU, Barcode, UPC Barcode, Name Short */}
            {/* <Grid item xs={12} sm={3}>
              <Typography style={{ paddingLeft: "4px" }}>PLU</Typography>
              <TextfieldDefaultNew
                id="product-plu"
                name="product-plu"
                type="text"
                value={productPlu}
                onChange={(e: any) => setProductPlu(e.target.value)}
                placeholder="Enter PLU"
              />
            </Grid> */}

            <Grid item xs={12} sm={3}>
              <Typography style={{ paddingLeft: "4px" }}>Barcode</Typography>
              <TextfieldDefaultNew
                id="product-barcode"
                name="product-barcode"
                type="text"
                value={productBarcode}
                onChange={(e: any) => setProductBarcode(e.target.value)}
                placeholder="Enter barcode"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography style={{ paddingLeft: "4px" }}>
                UPC Barcode
              </Typography>
              <TextfieldDefaultNew
                id="product-upc"
                name="product-upc"
                type="text"
                value={productUpcBarcode}
                onChange={(e: any) => setProductUpcBarcode(e.target.value)}
                placeholder="Enter UPC barcode"
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <Typography style={{ paddingLeft: "4px" }}>Name Short</Typography>
              <TextfieldDefaultNew
                id="product-name-short"
                name="product-name-short"
                type="text"
                value={productNameShort}
                onChange={(e: any) => setProductNameShort(e.target.value)}
                placeholder="Enter short name"
              />
            </Grid>

            {/* Row 7: Additional API fields - ExtId, GlobalProductId, ClassificationId */}
            {/* <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>Ext ID</Typography>
              <TextfieldDefaultNew
                id="product-ext-id"
                name="product-ext-id"
                type="text"
                value={productExtId}
                onChange={(e: any) => setProductExtId(e.target.value)}
                placeholder="Enter Ext ID"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Global Product ID
              </Typography>
              <TextfieldDefaultNew
                id="product-global-id"
                name="product-global-id"
                type="text"
                value={productGlobalProductId}
                onChange={(e: any) => setProductGlobalProductId(e.target.value)}
                placeholder="Enter Global Product ID"
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography style={{ paddingLeft: "4px" }}>
                Classification ID
              </Typography>
              <TextfieldDefaultNew
                id="product-classification-id"
                name="product-classification-id"
                type="text"
                value={productClassificationId}
                onChange={(e: any) =>
                  setProductClassificationId(e.target.value)
                }
                placeholder="Enter Classification ID"
              />
            </Grid> */}
          </Grid>

          <Box
            display="flex"
            justifyContent="flex-end"
            style={{ gap: "12px" }}
            mt={3}
          >
            <ButtonCommon
              onClick={handleCloseProductAddModal}
              color="yellow"
              style={{ width: "100px" }}
            >
              Cancel
            </ButtonCommon>
            <ButtonCommon
              onClick={handleAddProduct}
              color="green"
              isLoadingPage={isLoadingButton}
              style={{ width: "160px" }}
            >
              Add Product
            </ButtonCommon>
          </Box>
        </Box>
      </DialogCommonDefault>

      {/* Success/Error alerts */}
      <DefaultAlert
        open={!!success}
        handleClose={() => setSuccess("")}
        message={success}
        severity="success"
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default ProductsSection;
